import React, { useState, useRef, useEffect } from "react";
import type { Topic } from "./LMBuilder/types";
import ApiService from "../_services/ApiService";

interface TopicMultiSelectProps {
  selectedTopics: Topic[];
  onChange: (topics: Topic[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const TopicMultiSelect: React.FC<TopicMultiSelectProps> = ({
  selectedTopics = [],
  onChange,
  placeholder = "Search and select topics...",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Topic[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [enrichedTopics, setEnrichedTopics] = useState<Topic[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Enrich selected topics with course information
  useEffect(() => {
    const enrichTopics = async () => {
      const topicsToEnrich = selectedTopics.filter(
        topic => !topic.displayName && !topic.courseName
      );

      if (topicsToEnrich.length === 0) {
        setEnrichedTopics(selectedTopics);
        return;
      }

      try {
        // Fetch each topic to get course info
        const enriched = await Promise.all(
          selectedTopics.map(async (topic) => {
            if (topic.displayName || topic.courseName) {
              return topic;
            }

            // Search for this specific topic to get enriched data
            try {
              const response = await ApiService.searchTopics(topic.name);
              const responseData = response.data as any;
              const topics = responseData?.data || [];
              const enrichedTopic = topics.find((t: Topic) => t.id === topic.id);

              if (enrichedTopic) {
                return { ...topic, ...enrichedTopic };
              }
            } catch (error) {
              console.error(`[TopicMultiSelect] Error enriching topic ${topic.id}:`, error);
            }

            return topic;
          })
        );

        setEnrichedTopics(enriched);
      } catch (error) {
        console.error('[TopicMultiSelect] Error enriching topics:', error);
        setEnrichedTopics(selectedTopics);
      }
    };

    enrichTopics();
  }, [selectedTopics]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Search topics with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await ApiService.searchTopics(searchQuery);
        const responseData = response.data as any;
        const topics = responseData?.data || responseData || [];
        setSearchResults(Array.isArray(topics) ? topics : []);
      } catch (error) {
        console.error('[TopicMultiSelect] Error searching topics:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const isTopicSelected = (topicId: number) =>
    selectedTopics.some((t) => t.id === topicId);

  const toggleTopic = (topic: Topic) => {
    if (isTopicSelected(topic.id)) {
      onChange(selectedTopics.filter((t) => t.id !== topic.id));
    } else {
      onChange([...selectedTopics, topic]);
    }
    setSearchQuery(""); // Clear search after selection
  };

  const removeTopic = (topicId: number) => {
    onChange(selectedTopics.filter((t) => t.id !== topicId));
  };

  const clearAll = () => {
    onChange([]);
  };

  const getTopicDisplayName = (topic: Topic) => {
    // Use displayName from API if available (includes course info)
    if (topic.displayName) {
      return topic.displayName;
    }
    // Fallback to constructing display name with course name
    if (topic.courseName) {
      return `${topic.name} (${topic.courseName})`;
    }
    return topic.name || topic.topicCode || topic.code || `Topic ${topic.id}`;
  };

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="tag-multi-select" ref={dropdownRef}>
      {/* Unified container with chips and inline search input */}
      <div
        className="tag-multi-select-container"
        onClick={() => !disabled && inputRef.current?.focus()}
        style={{ cursor: disabled ? 'default' : 'text' }}
      >
        <div className="tag-chips-container">
          {enrichedTopics.map((topic) => (
            <span key={topic.id} className="tag-chip">
              {getTopicDisplayName(topic)}
              {!disabled && (
                <button
                  type="button"
                  className="tag-chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTopic(topic.id);
                  }}
                  aria-label="Remove topic"
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {/* Inline search input */}
          {!disabled && (
            <input
              ref={inputRef}
              type="text"
              className="tag-inline-input"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={selectedTopics.length === 0 ? placeholder : ""}
              disabled={disabled}
              style={{ flex: 1, minWidth: '120px', border: 'none', outline: 'none' }}
            />
          )}
        </div>
        {!disabled && selectedTopics.length > 0 && (
          <button
            type="button"
            className="tag-clear-all"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            aria-label="Clear all"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Dropdown Options */}
      {isOpen && !disabled && (
        <div className="tag-dropdown">
          {isSearching ? (
            <div className="tag-dropdown-item tag-dropdown-empty">
              Searching...
            </div>
          ) : searchQuery.trim().length < 2 ? (
            <div className="tag-dropdown-item tag-dropdown-empty">
              Type at least 2 characters to search
            </div>
          ) : searchResults.length === 0 ? (
            <div className="tag-dropdown-item tag-dropdown-empty">
              No topics found for "{searchQuery}"
            </div>
          ) : (
            searchResults.map((topic) => (
              <div
                key={topic.id}
                className={`tag-dropdown-item ${
                  isTopicSelected(topic.id) ? "tag-selected" : ""
                }`}
                onClick={() => toggleTopic(topic)}
              >
                <input
                  type="checkbox"
                  checked={isTopicSelected(topic.id)}
                  readOnly
                  className="tag-checkbox"
                />
                <span className="tag-name">{getTopicDisplayName(topic)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TopicMultiSelect;
